import { z } from "zod"

import { zAssetId, zVideoId } from "../../types/branded"

const zSha256 = z.string().regex(/^sha256:[a-f0-9]{64}$/)
const zVersion = z.string().regex(/^\d+\.\d+\.\d+$/)

export const zShotAssetInput = z.object({ assetId: zAssetId, sha256: zSha256 })

export const zOpeningInputs = z.object({
  plate: zShotAssetInput,
  post: zShotAssetInput,
  logo: zShotAssetInput,
})

export const zOpeningParameters = z
  .object({
    durationSec: z.number().min(1).max(8).default(5.1),
    postX: z.number().min(-1080).max(1080).default(149),
    postY: z.number().min(-1920).max(1920).default(641),
    postWidth: z.number().min(1).max(2160).default(773),
    postHeight: z.number().min(1).max(3840).default(617),
    logoX: z.number().min(-1080).max(1080).default(762),
    logoY: z.number().min(-1920).max(1920).default(30),
    logoWidth: z.number().min(1).max(2160).default(288),
    logoHeight: z.number().min(1).max(3840).default(75),
    postEntranceSec: z.number().min(0).max(8).default(0.28),
    entranceDurationSec: z.number().min(0.05).max(2).default(0.45),
    entranceFromScale: z.number().min(0.1).max(2).default(1.6),
    entranceOvershootScale: z.number().min(1).max(2).default(1.06),
    pushStartSec: z.number().min(0).max(8).default(1),
    pushDurationSec: z.number().min(0.1).max(7).default(4.1),
    pushScale: z.number().min(1).max(3).default(2.1),
    pushTargetX: z.number().min(-1080).max(1080).default(-90),
    pushTargetY: z.number().min(-1920).max(1920).default(90),
  })
  .refine((p) => p.postEntranceSec + p.entranceDurationSec <= p.durationSec, "Entrance exceeds duration")
  .refine((p) => p.pushStartSec + p.pushDurationSec <= p.durationSec, "Push exceeds duration")

export const zPlanShotInput = z.object({
  videoId: zVideoId,
  recipeId: z.string().min(1),
  methodId: z.string().min(1),
  inputs: z.record(z.string(), z.union([zShotAssetInput, z.string(), z.number(), z.boolean()])),
  parameters: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])),
  audioPolicy: z.enum(["silent", "supplied_exact", "generated_speech"]),
})

export const zRunShotInput = z.object({
  videoId: zVideoId,
  planHash: zSha256,
  requestId: z.string().trim().min(1).max(128),
  maxUsd: z.number().nonnegative(),
})

export const zGetShotRunInput = z.object({ videoId: zVideoId, runId: z.string().uuid() })
export const zShotRecipeInput = z.object({ recipeId: z.string().min(1) })

export const zShotPlan = z.object({
  hash: zSha256,
  input: zPlanShotInput,
  recipeVersion: zVersion,
  recipeHash: zSha256,
  methodVersion: zVersion,
  methodHash: zSha256,
  createdAt: z.string(),
  costUsd: z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("unknown") }),
    z.object({ kind: z.literal("bounded"), upperBoundUsd: z.number().nonnegative(), authority: z.string().min(1) }),
  ]),
})

export const zShotOccurrence = z
  .object({
    id: z.string().min(1),
    recipeId: z.string().min(1),
    recipeVersion: zVersion,
    sourceStartSec: z.number().nonnegative(),
    sourceEndSec: z.number().positive(),
    beatIds: z.array(z.string()),
    bindings: z.record(z.string(), zShotAssetInput),
    parameterOverrides: z.record(z.string(), z.number()),
  })
  .refine((value) => value.sourceEndSec > value.sourceStartSec, "Invalid source window")

export const zShotRun = z.object({
  id: z.string().uuid(),
  requestId: z.string().min(1),
  recipeId: z.string().min(1),
  recipeVersion: zVersion,
  recipeHash: zSha256,
  methodId: z.string().min(1),
  methodVersion: zVersion,
  methodHash: zSha256,
  planHash: zSha256,
  plan: zPlanShotInput,
  status: z.enum(["planned", "submitting", "pending", "completed", "failed", "submission_unknown"]),
  createdAt: z.string(),
  jobId: z.string().optional(),
  outputAssetId: zAssetId.optional(),
  outputUrl: z.string().optional(),
  measuredDurationSec: z.number().positive().optional(),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
  fps: z.number().positive().optional(),
  error: z.string().optional(),
})

export type IShotRun = z.infer<typeof zShotRun>
export type IShotPlan = z.infer<typeof zShotPlan>
export type TPlanShotInput = z.infer<typeof zPlanShotInput>
export type TOpeningParameters = z.infer<typeof zOpeningParameters>

export interface IShotRecipe {
  id: string
  version: string
  contentHash: string
  name: string
  description: string
  tags: string[]
  inputSlots: Array<{ id: string; required: boolean; mediaType: "image" | "video" | "audio" | "scalar" }>
  parameterDefaults: Record<string, string | number | boolean>
  output: {
    mediaType: "video"
    width: number
    height: number
    fps: number
    audio: "silent" | "supplied_exact" | "generated_speech"
  }
  qualityRubric: string[]
  exampleRefs: string[]
}

export interface IShotMethod {
  id: string
  version: string
  contentHash: string
  recipeIds: string[]
  plannerId: string
  executorId: string
  modelSlots: Array<{ id: string; kind: "video" | "image" | "audio" | "lipsync"; required: boolean }>
  output: IShotRecipe["output"]
}
